<template>
  <div
    class="absolute w-sidebar h-full flex flex-col items-center z-100 transition-position duration-300 group"
    :class="[
      showSidebar ? 'left-0' : '-left-sidebar',
      isDarkMode ? 'text-app-color' : 'text-dark-600',
      (isBooksPage || (isBookPage && isPaperShape)) && !isDarkMode ? 'bg-white' : 'bg-book',
    ]"
  >
    <div v-if="showInstructions" class="absolute w-full h-full z-10 bg-onboarding-backdrop" />

    <div v-if="showSidebar && !isBooksPage" class="hidden md:flex opacity-0 group-hover:opacity-100 absolute top-10 -right-15 w-30 h-30 hover:bg-primary rounded-full anim-all cursor-pointer"
         :class="isDarkMode ? '' : 'hover:shadow-dropdown' "
         @click="toggleSidebar"
    >
      <i class="icon-focus-mode m-auto" />
    </div>

    <teleport to="body">
      <div
        v-if="showSidebar && isMobile"
        class="fixed top-0 z-40 w-full h-full"
        :class="isBookPage ? 'left-338' : 'left-sidebar'"
        @click="close"
      />
    </teleport>

    <div id="main-sidebar" class="flex flex-col flex-grow w-full border-r pb-20 px-13" :class="isDarkMode ? 'border-project' : 'border-grey-575'">
      <div class="mt-30 cursor-pointer text-center" @click="backToBooks">
        <svg class="inline stroke-current text-app-color w-26 h-auto" width="22" height="28" viewBox="0 0 22 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4.95489 13.9146C5.52013 10.2887 7.00232 0.244984 8.40914 1.0452C10.1677 2.04546 7.49848 12.0391 8.40914 13.2478C9.31981 14.4564 10.9281 4.8289 12.718 4.03703C14.508 3.24515 11.832 14.9149 11.361 15.4567C10.8899 15.9985 16.9792 6.59533 17.2618 8.97096C17.4637 10.6686 15.5281 15.5284 14.4187 17.9525C14.3033 18.2048 14.2456 18.3309 14.2701 18.3581C14.277 18.3657 14.2856 18.3711 14.2955 18.374C14.3306 18.3845 14.4191 18.2784 14.5961 18.0663C16.2804 16.0482 19.9511 11.7397 20.8788 12.5605C21.9622 13.5191 15.5878 26.7143 9.46275 26.7143C2.71546 26.7143 1.71855 16.3003 1.18778 13.9146C-0.180567 7.76442 6.3782 13.9438 6.3782 17.7989" stroke-width="1.38937" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="flex flex-col justify-between flex-grow">
        <div class="flex flex-col items-center">
          <ButtonModule
            prefix="icon-search text-17"
            tooltip-text="Search"
            is-white-tooltip
            tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-25"
            tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
            tooltip-bg-opacity="bg-opacity-80"
            class="relative p-9 rounded-6 mt-30"
            :class="[btnClasses(showSearch), isDarkMode ? 'bg-book' : 'hover:bg-grey-400']"
            @click="openSearchModal"
          >
            <template #tooltip>
              <div>
                <span class="mr-5">Search</span>
                <span class="bg-project px-7 py-3 rounded-8 text-12">Ctrl K</span>
              </div>
            </template>
          </ButtonModule>
          <ButtonModule
            prefix="icon-workspace text-19"
            tooltip-text="Spaces"
            is-white-tooltip
            tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-25"
            tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
            tooltip-bg-opacity="bg-opacity-80"
            class="relative mt-10 p-7 rounded-6"
            :class="[btnClasses(isBooksPage), isDarkMode ? 'bg-book' : 'hover:bg-grey-400']"
            @click="backToBooks"
          />
          <ButtonModule
            v-if="false"
            prefix="icon-today text-21"
            tooltip-text="Today"
            is-white-tooltip
            tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-25"
            tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
            tooltip-bg-opacity="bg-opacity-80"
            class="relative mt-10 p-7 rounded-6"
            :class="[btnClasses(), isDarkMode ? 'bg-book' : 'hover:bg-grey-400']"
          />
        </div>

        <div class="flex flex-col items-center">
          <Inbox v-if="isTeamAccount" />

          <ButtonModule
            v-if="!isRegularUser && isTeamAccount"
            tooltip-text="Invite team members"
            is-white-tooltip
            tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-25"
            tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
            tooltip-bg-opacity="bg-opacity-80"
            class="relative p-0 mt-12"
            @click="openInviteModal"
          >
            <template #default>
              <img class="h-31 max-w-full cursor-pointer" src="@/assets/images/invite_teammates.svg" alt="teammates">
            </template>
          </ButtonModule>

          <UserMenu />
        </div>
      </div>
    </div>
    <SearchBar />

    <InviteTeammates v-if="showInviteModal" @close="closeInviteModal" />
  </div>
</template>

<script>
import { defineComponent, computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import localStorageService from '@/core/local-storage'
import { preventBodyScroll } from '@/core/helper-functions'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import InviteTeammates from '@/components/books/InviteTeammates'
import UserMenu from './UserMenu'
import Inbox from './Inbox'
import SearchBar from '@/components/shared/SearchBar'

export default defineComponent({
  components: { ButtonModule, InviteTeammates, UserMenu, Inbox, SearchBar },

  setup () {
    const { state, getters, commit } = useStore()
    const route = useRoute()
    const router = useRouter()

    const showInviteModal = ref(false)

    const team = computed(() => state.team.team)

    const isTeamAccount = computed(() => getters.isTeamAccount)
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isRegularUser = computed(() => getters.isRegularUser)
    const showSidebar = computed(() => state.settings.showSidebar)
    const showSearch = computed(() => state.general.showSearch)
    const isPaperShape = computed(() => getters.isPaperShape)

    const chatMutedAt = computed(() => state.chat.chatMutedAt)

    const isHeadquarters = computed(() => route.name === 'Headquarters')
    const isBooksPage = computed(() => route.name === 'Books' || route.name === 'TeamBooks')
    const isBookPage = computed(() => route.name === 'Book' || route.name === 'TeamBook' || route.name === 'Headquarters')
    const isTaskBoard = computed(() => route.name === 'TaskBoard' || route.name === 'TaskBoardTeam')
    const isWeekPlanner = computed(() => route.name === 'WeekPlanner' || route.name === 'WeekPlannerTeam')
    const isDiscussion = computed(() => route.name === 'Discussions' || route.name === 'Discussion')

    const showInstructions = computed(() => {
      const showOnProject = !getters.isProjectInstructionAccepted && state.profile.activeStep > 1 && isBookPage.value

      return (!getters.isBookInstructionAccepted && isBooksPage.value) || showOnProject
    })

    const btnClasses = computed(() => value => {
      const prefix = value ? '' : 'hover:'
      const lightModeClasses = value ? 'border border-brand text-brand bg-grey-200 shadow-sidebar' : 'border border-transparent'

      return isDarkMode.value ? `${prefix}bg-project ${prefix}text-white border border-transparent` : lightModeClasses
    })

    function backToBooks () {
      localStorageService.removeItem('lastOpenedProject')

      isTeamAccount.value
        ? router.push({ name: 'TeamBooks', params: { team: team.value.link } })
        : router.push({ name: 'Books' })

      if (isMobile.value) {
        commit('updateSidebar', false)
      }
    }

    function openSearchModal () {
      commit('toggleSearch', true)
    }

    function openInviteModal () {
      showInviteModal.value = true
      preventBodyScroll(true)
    }

    function closeInviteModal () {
      showInviteModal.value = false
      preventBodyScroll(false)
    }

    function close () {
      if (isMobile.value) {
        commit('updateSidebar', false)
      }
    }

    function toggleSidebar () {
      commit('updateSidebar', !showSidebar.value)
    }

    return {
      // ref
      // data
      showInviteModal,
      // computed
      isMobile,
      showSearch,
      isDarkMode,
      btnClasses,
      isBookPage,
      isBooksPage,
      isTaskBoard,
      chatMutedAt,
      showSidebar,
      isPaperShape,
      isDiscussion,
      isTeamAccount,
      isWeekPlanner,
      isRegularUser,
      isHeadquarters,
      showInstructions,

      // function
      close,
      backToBooks,
      toggleSidebar,
      openSearchModal,
      openInviteModal,
      closeInviteModal
    }
  }
})
</script>
